<template>
  <div class="pricing-view">
    <v-container>
      <v-row>
        <v-col cols="12" class="text-center">
          <h1 class="text-h3 mb-2">Pricing Plans</h1>
          <p class="subtitle-1" :class="guaranteeInfo ? 'mb-2' : 'mb-8'">Choose the plan that works best for you</p>
          <p v-if="guaranteeInfo" class="body-2 mb-8">
            <v-icon small color="success">mdi-shield-check</v-icon>
            <strong>{{ guaranteeInfo.short }}.</strong> {{ guaranteeInfo.long }}
            <a :href="refundPolicyUrl" target="_blank" rel="noopener">Refund policy</a>
          </p>
        </v-col>
      </v-row>

      <!-- No billing toggle needed anymore as we only offer monthly plans -->

      <!-- Plans -->
      <v-row justify="center">
        <v-col
          v-for="plan in plans"
          :key="plan.id"
          cols="12"
          md="6"
          :lg="planColLg"
        >
          <subscription-plan-card
            :plan="plan"
            :current-plan="currentPlan"
            :scheduled-plan="scheduledPlanName"
            :scheduled-plan-label="scheduledPlanLabel"
            :highlight-feature="highlightFeature"
            @select-plan="askPlanChange"
            @free-plan-selected="askFreeDowngrade"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="text-center">
          <p class="caption grey--text mb-1">
            Prices are in {{ ($config.currency || 'usd').toUpperCase() }}. Taxes may be
            calculated at checkout. Subscriptions renew monthly until cancelled.
          </p>
          <!-- [TODO: legal review] -->
          <p class="caption grey--text mb-0">{{ resultsDisclaimer }}</p>
        </v-col>
      </v-row>

      <!-- FAQ Section -->
      <v-row class="mt-12">
        <v-col cols="12">
          <h2 class="text-h4 text-center mb-8">Frequently Asked Questions</h2>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-card outlined class="mb-4">
            <v-card-title>{{ $config.CREDITS_FAQ_QUESTION || 'What are credits?' }}</v-card-title>
            <v-card-text>
              {{ creditsExplainer }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="mb-4">
            <v-card-title>{{ $config.PRICING_USAGE_CALC_QUESTION || 'How are credits calculated?' }}</v-card-title>
            <v-card-text>
              {{ creditsCalculationExplainer }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="mb-4">
            <v-card-title>{{ $config.PRICING_USAGE_RENEWAL_QUESTION || 'When do my credits renew?' }}</v-card-title>
            <v-card-text>
              {{ $config.PRICING_USAGE_RENEWAL_EXPLAINER || 'Included monthly credits reset each billing cycle on your subscription renewal date and do not roll over to the next month.' }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="mb-4">
            <v-card-title>Can I cancel my subscription?</v-card-title>
            <v-card-text>
              {{ $config.SUBSCRIBE_CANCEL_FAQ || 'Yes, you can cancel your subscription at any time. You will continue to have access to your current plan until the end of the billing period.' }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Plan change confirmation dialog -->
    <v-dialog v-model="confirmDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ confirmTitle }}</v-card-title>
        <v-card-text class="text-body-2" style="white-space: pre-line">{{
          confirmText
        }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="busy" @click="confirmDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary" :loading="busy" @click="runConfirmedAction">{{
            confirmCta
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success / info toast (green) -->
    <v-snackbar v-model="snackbar" color="success" :timeout="3500">{{
      snackbarText
    }}</v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="5000">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <v-overlay :value="isPostCheckoutSyncing" opacity="0.92" z-index="2000">
      <div class="post-checkout-sync">
        <v-progress-circular
          indeterminate
          size="64"
          width="6"
          color="primary"
          class="mb-4"
        />
        <div
          class="
            post-checkout-sync__message
            text-h6
            font-weight-medium
            text-center
          "
        >
          {{ syncingMessage }}
        </div>
      </div>
    </v-overlay>
  </div>
</template>

<script>
import { formatPlanPrice } from "@shared/utils/planPricing";
import { mapGetters, mapActions } from "vuex";
import SubscriptionPlanCard from "@shared/components/SubscriptionPlanCard.vue";
import api from "@shared/services/api";
import { formatPlanName } from "@shared/utils/planLabels";
import { guarantee, refundPolicyUrl, checkoutNote, downgradeNote } from "@shared/utils/guarantee";
import { isMember, registerFirst, serverMessage } from "@shared/utils/memberFunnel";
import { resultsDisclaimer } from "@shared/utils/productWording";

export default {
  name: "PricingView",
  components: {
    SubscriptionPlanCard
  },
  data() {
    return {
      selectedPlan: null,
      errorSnackbar: false,
      errorMessage: "",
      snackbar: false,
      snackbarText: "",
      confirmDialog: false,
      confirmTitle: "",
      confirmText: "",
      confirmCta: "Confirm",
      pendingAction: null,
      busy: false,
      isPostCheckoutSyncing: false,
      syncingMessage: ""
    };
  },
  computed: {
    guaranteeInfo() {
      return guarantee();
    },
    refundPolicyUrl() {
      return refundPolicyUrl();
    },
    resultsDisclaimer() {
      return resultsDisclaimer();
    },
    ...mapGetters([
      "isAuthenticated",
      "availablePlans",
      "purchasablePlans",
      "currentSubscription",
      "loading",
      "error"
    ]),
    plans() {
      // Hides the Free plan when FORCE_PAID_PLANS is on; identical to
      // availablePlans otherwise.
      return this.purchasablePlans;
    },
    // Default copy names a "regenerate output you're not happy with" action
    // that doesn't exist on every app - apps without it override via config.
    creditsExplainer() {
      return (
        (this.$config && this.$config.PRICING_CREDITS_EXPLAINER) ||
        "Credits are SaaS usage units for " +
          this.$config.productName +
          " processing time. They are not money, stored value, a wallet balance, or transferable value, and cannot be withdrawn, transferred, resold, or redeemed for cash. Each job consumes credits based on processing time and compute complexity."
      );
    },
    creditsCalculationExplainer() {
      return (
        (this.$config && this.$config.PRICING_CREDITS_CALC_EXPLAINER) ||
        "Credits are consumed based on how much work a job takes to run. A larger job uses more credits than a small one. Regenerating output you are not happy with draws on credits as well."
      );
    },
    // The feature slug the referring CTA sent (a subscribe button elsewhere in
    // the app links to /pricing?feature=<slug>) - passed to the plan cards so
    // the rows for that feature are visually highlighted.
    highlightFeature() {
      var value = this.$route.query.feature;
      return typeof value === "string" ? value : "";
    },
    // The plan boxes were designed 4-across (lg="3" = 25%). When a plan is
    // hidden (FORCE_PAID_PLANS drops Free) we render fewer boxes, so widen each
    // to fill the row instead of leaving a left-aligned gap: 3 -> lg=4 (33%),
    // 2 -> lg=6 (50%), 1 -> lg=12, 4+ -> lg=3 (25%).
    planColLg() {
      const n = (this.plans && this.plans.length) || 0;
      if (n >= 4) return 3;
      if (n <= 1) return 12;
      return Math.floor(12 / n);
    },
    currentPlan() {
      if (!this.isAuthenticated) {
        return "";
      }

      return this.currentSubscription
        ? this.currentSubscription.subscription_plan
        : "";
    },
    isScheduledDowngradeToFree() {
      return (
        !!this.currentSubscription &&
        this.currentSubscription.subscription_status === "canceling" &&
        this.currentSubscription.subscription_plan &&
        this.currentSubscription.subscription_plan.toLowerCase() !== "free"
      );
    },
    scheduledPlanName() {
      return this.isScheduledDowngradeToFree ? "Free" : "";
    },
    scheduledPlanLabel() {
      if (!this.isScheduledDowngradeToFree) {
        return "Scheduled Next";
      }

      const endDate = this.formatDate(
        this.currentSubscription.subscription_end_date
      );
      return endDate === "N/A" ? "Scheduled Next" : `Scheduled for ${endDate}`;
    }
  },
  methods: {
    formatPlanPrice(plan) { return formatPlanPrice(plan, this.$config); },
    ...mapActions(["fetchPlans", "fetchSubscription", "createCheckoutSession"]),
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async fetchSubscriptionSnapshot() {
      const response = await api.getCurrentSubscription();
      return response.data;
    },
    hasSubscriptionChanged(previousSubscription, nextSubscription) {
      if (!previousSubscription || !nextSubscription) {
        return false;
      }

      return (
        previousSubscription.subscription_plan !==
          nextSubscription.subscription_plan ||
        previousSubscription.subscription_status !==
          nextSubscription.subscription_status ||
        previousSubscription.subscription_end_date !==
          nextSubscription.subscription_end_date
      );
    },
    clearCheckoutQueryParams() {
      sessionStorage.removeItem("pendingSubscriptionPlanName");
      const query = { ...this.$route.query };
      delete query.success;
      delete query.canceled;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    async syncPostCheckoutState() {
      if (this.isPostCheckoutSyncing) {
        return;
      }

      this.isPostCheckoutSyncing = true;
      this.syncingMessage = "Processing your subscription update...";
      const expectedPlanName = sessionStorage.getItem(
        "pendingSubscriptionPlanName"
      );
      const initialSubscription = this.currentSubscription
        ? { ...this.currentSubscription }
        : null;
      const maxAttempts = 8;
      const retryDelayMs = 1500;

      try {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const updatedSubscription = await this.fetchSubscriptionSnapshot();

          if (
            (expectedPlanName &&
              updatedSubscription &&
              updatedSubscription.subscription_plan &&
              updatedSubscription.subscription_plan.toLowerCase() ===
                expectedPlanName.toLowerCase()) ||
            this.hasSubscriptionChanged(
              initialSubscription,
              updatedSubscription
            )
          ) {
            await this.fetchSubscription();
            this.toast("Your subscription has been successfully updated!");
            return;
          }

          if (attempt < maxAttempts - 1) {
            await this.sleep(retryDelayMs);
          }
        }

        this.toast(
          "Your subscription update is still processing. Refresh in a few seconds if needed."
        );
      } catch (error) {
        console.error("Error syncing post-checkout subscription state:", error);
        this.toast(
          "Your checkout completed, but the subscription update is still syncing. Refresh in a few seconds if needed."
        );
      } finally {
        this.syncingMessage = "";
        this.isPostCheckoutSyncing = false;
      }
    },
    async syncDowngradeToFreeState(initialSubscription, successMessage) {
      if (this.isPostCheckoutSyncing) {
        return;
      }

      this.isPostCheckoutSyncing = true;
      this.syncingMessage = "Scheduling your downgrade...";
      const initialPlanName =
        initialSubscription &&
        initialSubscription.subscription_plan &&
        initialSubscription.subscription_plan.toLowerCase();
      const maxAttempts = 8;
      const retryDelayMs = 1500;

      try {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const updatedSubscription = await this.fetchSubscriptionSnapshot();
          const updatedPlanName =
            updatedSubscription &&
            updatedSubscription.subscription_plan &&
            updatedSubscription.subscription_plan.toLowerCase();
          const downgradeScheduled =
            !!updatedSubscription &&
            ((updatedPlanName === "free" &&
              initialPlanName &&
              initialPlanName !== "free") ||
              (updatedSubscription.subscription_status === "canceling" &&
                updatedPlanName === initialPlanName &&
                this.hasSubscriptionChanged(
                  initialSubscription,
                  updatedSubscription
                )));

          if (downgradeScheduled) {
            await this.fetchSubscription();
            this.toast(
              successMessage ||
                "Your subscription will downgrade to the Free plan at the end of the current billing period."
            );
            return;
          }

          if (attempt < maxAttempts - 1) {
            await this.sleep(retryDelayMs);
          }
        }

        await this.fetchSubscription();
        this.toast(
          successMessage ||
            "Your downgrade request was submitted, but the updated subscription state is still syncing. Refresh in a few seconds if needed."
        );
      } catch (error) {
        console.error("Error syncing free-plan downgrade state:", error);
        this.toast(
          successMessage ||
            "Your downgrade request was submitted, but the updated subscription state is still syncing. Refresh in a few seconds if needed."
        );
      } finally {
        this.syncingMessage = "";
        this.isPostCheckoutSyncing = false;
      }
    },
    async checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("success") === "true") {
        await this.syncPostCheckoutState();
        this.clearCheckoutQueryParams();
      } else if (urlParams.get("canceled") === "true") {
        this.toast(
          "Checkout was canceled. Your subscription remains unchanged."
        );
        this.clearCheckoutQueryParams();
      }
    },
    toast(text) {
      this.snackbarText = text;
      this.snackbar = true;
    },
    planPriceByName(name) {
      if (!name) return 0;
      var lower = String(name).toLowerCase();
      var plans = this.plans || [];
      var match = plans.find(function (p) {
        return (p.name || "").toLowerCase() === lower;
      });
      return match ? Number(match.price_monthly || 0) : 0;
    },
    hasActivePaid() {
      var s = this.currentSubscription;
      return (
        !!s &&
        !!s.subscription_plan &&
        s.subscription_plan.toLowerCase() !== "free" &&
        ["active", "canceling"].indexOf(s.subscription_status || "") !== -1
      );
    },
    askPlanChange(plan) {
      // Signed-out visitors and anonymous-trial guests cannot pay: register
      // first, then continue to the plan page with this plan picked.
      if (!isMember(this.$store)) {
        this.$router.push(registerFirst("/subscribe", plan.name)).catch(() => {});
        return;
      }

      // Already on this plan - info, not a confirm flow.
      if (
        this.currentPlan &&
        plan.name.toLowerCase() === this.currentPlan.toLowerCase()
      ) {
        this.toast(`You are already on the ${formatPlanName(plan.name)} plan.`);
        return;
      }

      var planLabel = formatPlanName(plan.name || "");
      var price = this.formatPlanPrice(plan) + "/month";
      if (!this.hasActivePaid()) {
        this.confirmTitle = `Start the ${planLabel} plan`;
        this.confirmText = `You'll be taken to Stripe to start your ${planLabel} subscription at ${price}. ${checkoutNote()}`;
        this.confirmCta = "Continue to Stripe";
      } else if (
        Number(plan.price_monthly || 0) >
        this.planPriceByName(this.currentSubscription.subscription_plan)
      ) {
        this.confirmTitle = `Upgrade to ${planLabel}?`;
        this.confirmText = `You'll be upgraded to ${planLabel} (${price}) immediately. We'll charge the difference for the days remaining in your current billing period to your existing payment method - you won't be double-charged.`;
        this.confirmCta = "Upgrade now";
      } else {
        this.confirmTitle = `Switch to ${planLabel}?`;
        this.confirmText = `You'll be switched to ${planLabel} (${price}) right away. Your next charge will be at the new lower price${downgradeNote()}`;
        this.confirmCta = "Switch now";
      }
      var self = this;
      this.pendingAction = function () {
        return self.selectPlan(plan);
      };
      this.confirmDialog = true;
    },
    askFreeDowngrade() {
      if (!isMember(this.$store)) {
        this.$router.push("/register").catch(() => {});
        return;
      }

      if (this.currentPlan && this.currentPlan.toLowerCase() === "free") {
        this.toast("You are already on the Free plan.");
        return;
      }

      var endDate =
        this.currentSubscription &&
        this.currentSubscription.subscription_end_date
          ? new Date(
              this.currentSubscription.subscription_end_date
            ).toLocaleDateString()
          : "";
      var effective = endDate || "the end of the billing period";
      this.confirmTitle = "Switch to the Free plan?";
      this.confirmText = `Your paid plan will be cancelled at the end of your current billing period (${effective}), and you'll switch to the Free plan then. You keep your current plan until then.`;
      this.confirmCta = "Switch to Free";
      var self = this;
      this.pendingAction = function () {
        return self.selectFreePlan();
      };
      this.confirmDialog = true;
    },
    async runConfirmedAction() {
      var action = this.pendingAction;
      if (!action) {
        this.confirmDialog = false;
        return;
      }
      await action();
    },
    selectPlan(plan) {
      this.selectedPlan = plan;
      this.proceedToCheckout();
    },

    async selectFreePlan() {
      const initialSubscription = this.currentSubscription
        ? { ...this.currentSubscription }
        : null;

      this.busy = true;
      try {
        const response = await this.$store.dispatch("downgradeToFreePlan");
        this.confirmDialog = false;
        await this.syncDowngradeToFreeState(
          initialSubscription,
          response.message
        );
      } catch (error) {
        console.error("Error downgrading to free plan:", error);
        this.errorMessage =
          "Failed to downgrade to the Free plan. Please try again.";
        this.errorSnackbar = true;
      } finally {
        this.busy = false;
      }
    },
    proceedToCheckout() {
      if (!this.selectedPlan) return;

      if (!isMember(this.$store)) {
        this.$router
          .push(registerFirst("/subscribe", this.selectedPlan.name))
          .catch(() => {});
        return;
      }

      // Create success and cancel URLs
      const successUrl = `${window.location.origin}/subscription?success=true`;
      const cancelUrl = `${window.location.origin}/subscription?canceled=true`;

      this.busy = true;
      return this.createCheckoutSession({
        planId: this.selectedPlan.id,
        billingCycle: "monthly", // Always use monthly billing
        successUrl,
        cancelUrl
      })
        .then(response => {
          if (response.message) {
            this.confirmDialog = false;
            this.toast(response.message);
            this.fetchSubscription();
          } else {
            sessionStorage.setItem(
              "pendingSubscriptionPlanName",
              this.selectedPlan.name
            );
            window.location.href = response.checkout_url;
          }
        })
        .catch(error => {
          console.error("Error creating checkout session:", error);
          this.errorMessage = serverMessage(
            error,
            "Failed to create checkout session. Please try again."
          );
          this.errorSnackbar = true;
        })
        .finally(() => {
          this.busy = false;
        });
    }
  },
  async created() {
    await this.fetchPlans();

    if (this.isAuthenticated) {
      await this.fetchSubscription();
      await this.checkUrlParams();
    }
  }
};
</script>

<style scoped>
.pricing-view {
  padding-bottom: 2rem;
}

.pricing-card {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.pricing-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px var(--shadow-xs);
}

.popular-plan {
  border: 2px solid var(--color-info-dark) !important;
  transform: scale(1.02);
}

.popular-plan:hover {
  transform: translateY(-5px) scale(1.02);
}

.popular-badge {
  position: absolute;
  top: 10px;
  right: -30px;
  background-color: var(--color-info-dark);
  color: var(--text-primary);
  padding: 5px 30px;
  transform: rotate(45deg);
  font-size: 0.8rem;
  font-weight: bold;
  box-shadow: 0 2px 4px var(--shadow-md);
}

.post-checkout-sync {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 420px;
  padding: 24px;
}

.post-checkout-sync__message {
  color: var(--text-primary);
}
</style>
